import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';

const LoginPage = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }
    
    setIsLoading(true);
    try {
      const user = await login(username, password);
      toast.success(`Welcome back, ${user.entity_name || user.username}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container" data-testid="login-page">
      <div className="w-full max-w-md px-4">
        {/* Logo and Header */}
        <div className="text-center mb-8 animate-fadeIn">
          <img 
            src="https://bondmaster.com.in/logoBond.png" 
            alt="BondMaster Logo" 
            className="h-16 mx-auto mb-4"
            data-testid="login-logo"
          />
          <p className="text-[#475569] mt-2">
            Your trusted bond investment partner
          </p>
        </div>

        {/* Login Card */}
        <Card className="glass-card border-0 shadow-xl animate-fadeIn" style={{ animationDelay: '0.1s' }}>
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-heading text-center text-[#1B4D3E]">
              Sign In
            </CardTitle>
            <CardDescription className="text-center text-[#64748b]">
              Enter your credentials to access your portfolio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-[#1B4D3E] font-medium">
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-11 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#1B4D3E]/20"
                  data-testid="login-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1B4D3E] font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#1B4D3E]/20"
                  data-testid="login-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 bg-[#1B4D3E] hover:bg-[#163d32] text-white font-medium rounded-lg transition-all duration-200"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></span>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="h-4 w-4" />
                    Sign In
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-[#94a3b8] mt-8">
          © 2025 BondMaster. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
